import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import { normalizeFileName } from '../../../../components/ui/utils/fileNameEncoding';
import { renderSplitViewBadgeOrBoolean } from '../../../../components/generic/splitViewFieldDisplay';

const ProductStatusChangeDetailsTab = ({ 
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  onRecordUpdate
}) => {
  const { theme } = useTheme();
  
  if (!selectedRecord) {
    return (
      <div className={cn("p-4 text-center", theme.text.muted)}>
        No record selected
      </div>
    );
  }

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }      }
    >
      <div className="grid grid-cols-1 gap-4">
        {config?.formSections?.map((section, idx) => {
          // Skip custom component sections - they're handled separately
          if (section.layout === 'custom' && section.customComponent) {
            return null;
          }
          
          // Handle File Attachments section
          if ((section.title === 'File Attachments' || section.label === 'File Attachments') || section.name === 'fileAttachments') {
            const activeFiles = selectedRecordFiles?.filter(file => !file.isDeleted) || [];
            if (!config?.features?.fileAttachments || !activeFiles || activeFiles.length === 0) {
              return null;
            }
            return (
              <div key={idx}>
                <div className="flex items-center gap-2 mb-3">
                  {section.icon && (
                    <span className="text-base" aria-hidden="true">{section.icon}</span>
                  )}
                  <div className={cn("text-sm font-semibold", theme.text.primary)}>
                    {section.title || section.label || 'File Attachments'} ({activeFiles.length})
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {activeFiles.map((file, index) => (
                    <div key={index} className={cn("flex items-center gap-3 p-3 rounded-lg border", theme.colors.background, theme.border.subtle)}>
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                          {normalizeFileName(file.fileName || '')}
                        </div>
                        <div className={cn("text-xs", theme.text.secondary)}>
                          {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                        </div>
                      </div>
                      <a 
                        href={API_ENDPOINTS.fileAttachment.download(file.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("text-xs px-3 py-1 rounded-md transition-colors", theme.colors.primary, theme.text.inverse, "hover:opacity-80")}
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          
          // Regular field sections
          return (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-2">
                {section.icon && (
                  <span className="text-base" aria-hidden="true">{section.icon}</span>
                )}
                <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title || section.label}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.fields?.map((fieldName) => {
                  const f = fields?.find(ff => ff.name === fieldName);
                  if (!f || f.type === 'hidden') return null;
                  
                  let rawVal = selectedRecord[fieldName];
                  
                  // For qpUserSignedAt, check if it's truly empty (null, undefined, empty string, or invalid date)
                  if (fieldName === 'qpUserSignedAt') {
                    if (!rawVal || rawVal === null || rawVal === undefined || rawVal === '') {
                      return null;
                    }
                    // Try to parse as date to ensure it's valid
                    try {
                      const testDate = new Date(rawVal);
                      if (isNaN(testDate.getTime())) {
                        return null; // Invalid date, don't display
                      }
                    } catch {
                      return null; // Can't parse, don't display
                    }
                  }
                  
                  const isEmpty = rawVal == null || rawVal === "" || rawVal === undefined;

                  // Compute display value for select/lookups and special types
                  let displayVal = rawVal;
                  let isBadge = false;
                  let isStatus = false;
                  let isBoolean = false;

                  // Try table.displayField (e.g., status, productStatus, qpUser)
                  const displayField = f.table && f.table.displayField;
                  if (displayField && selectedRecord[displayField] != null) {
                    const displayFieldValue = selectedRecord[displayField];
                    // Handle user objects (e.g., qpUser, createdBy, etc.)
                    if (typeof displayFieldValue === 'object' && displayFieldValue !== null && !Array.isArray(displayFieldValue)) {
                      // For user objects, try multiple property names (check userDisplayName first for users)
                      if (displayFieldValue.userDisplayName) {
                        displayVal = displayFieldValue.userDisplayName;
                      } else if (displayFieldValue.userFirstName && displayFieldValue.userLastName) {
                        displayVal = `${displayFieldValue.userFirstName} ${displayFieldValue.userLastName}`.trim();
                      } else if (displayFieldValue.name) {
                        displayVal = displayFieldValue.name;
                      } else if (displayFieldValue.title) {
                        displayVal = displayFieldValue.title;
                      } else if (displayFieldValue.firstName && displayFieldValue.lastName) {
                        displayVal = `${displayFieldValue.firstName} ${displayFieldValue.lastName}`.trim();
                      } else if (displayFieldValue.userEmail) {
                        displayVal = displayFieldValue.userEmail;
                      } else if (displayFieldValue.email) {
                        displayVal = displayFieldValue.email;
                      } else {
                        displayVal = '—';
                      }
                      isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
                      isStatus = f.table && f.table.cellRenderer === 'statusBadge';
                    } else {
                      displayVal = displayFieldValue;
                      isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
                      isStatus = f.table && f.table.cellRenderer === 'statusBadge';
                    }
                  } else if (/Id$/.test(f.name)) {
                    // Try related object common patterns
                    const base = f.name.replace(/Id$/, '');
                    const rel = selectedRecord[base];
                    if (rel && typeof rel === 'object' && rel !== null && !Array.isArray(rel)) {
                      // Handle user objects - check userDisplayName first
                      if (rel.userDisplayName) {
                        displayVal = rel.userDisplayName;
                      } else if (rel.userFirstName && rel.userLastName) {
                        displayVal = `${rel.userFirstName} ${rel.userLastName}`.trim();
                      } else {
                        displayVal = rel.name || 
                                   rel.title || 
                                   (rel.firstName && rel.lastName ? `${rel.firstName} ${rel.lastName}`.trim() : null) ||
                                   rel.userEmail ||
                                   rel.email ||
                                   rel.status || 
                                   rel.label || 
                                   null;
                      }
                      // If we still don't have a value, don't show the raw ID
                      if (displayVal === null || displayVal === '') {
                        displayVal = '—';
                      }
                      isBadge = true;
                    } else if (rawVal != null && rel == null) {
                      // If we have an ID but no relation object, show dash
                      displayVal = '—';
                    }
                  }

                  // Type-based rendering
                  if ((f.type === 'switch') || f.type === 'boolean') {
                    isBoolean = true;
                  } else if (f.type === 'date') {
                    try { 
                      if (!isEmpty) displayVal = new Date(rawVal).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }); 
                    } catch {}
                  } else if (f.type === 'datetime' || f.type === 'datetime-local') {
                    try { 
                      // For datetime fields, always try to format if we have a value
                      if (rawVal) {
                        const dateValue = new Date(rawVal);
                        if (!isNaN(dateValue.getTime())) {
                          // Format as dd/mm/yyyy hh:mm
                          const day = String(dateValue.getDate()).padStart(2, '0');
                          const month = String(dateValue.getMonth() + 1).padStart(2, '0');
                          const year = dateValue.getFullYear();
                          const hours = String(dateValue.getHours()).padStart(2, '0');
                          const minutes = String(dateValue.getMinutes()).padStart(2, '0');
                          displayVal = `${day}/${month}/${year} ${hours}:${minutes}`;
                        } else {
                          // Invalid date, show raw value or dash
                          displayVal = rawVal || '—';
                        }
                      } else {
                        displayVal = '—';
                      }
                    } catch (error) {
                      console.warn(`Error formatting datetime for ${fieldName}:`, error, 'Raw value:', rawVal);
                      // If formatting fails, show the raw value
                      displayVal = rawVal || '—';
                    }
                  }

                  return (
                    <div key={fieldName}>
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                        {renderSplitViewBadgeOrBoolean({
                          field: f,
                          record: selectedRecord,
                          theme,
                          rawVal,
                          displayVal,
                          isBadge,
                          isStatus,
                          isBoolean,
                        }) ?? (f.type === 'textarea' ? (
                          <span className="whitespace-pre-wrap">{String(displayVal ?? '—')}</span>
                        ) : (
                          <span className={cn(isEmpty && theme.text.muted)}>{String(displayVal ?? '—')}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductStatusChangeDetailsTab;
